import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { ROLE_LABELS, MODULES, MODULE_ORDER } from "../lib/constants";
import { Avatar } from "../components/Badges";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Users, Plus, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";

const ROLE_BADGE = {
  pm: "bg-slate-900 text-white",
  dev: "bg-indigo-100 text-indigo-700",
  qa: "bg-purple-100 text-purple-700",
  stakeholder: "bg-slate-100 text-slate-600",
};

const DEFAULT_MODULES = {
  pm: ["dashboard", "desarrollos", "tareas", "deploys", "riesgos", "usuarios", "configuracion"],
  dev: ["dashboard", "desarrollos", "tareas"],
  qa: ["dashboard", "desarrollos", "tareas", "deploys"],
  stakeholder: ["dashboard", "deploys", "riesgos"],
};

const EMPTY = { email: "", name: "", role: "dev", password: "", modules: DEFAULT_MODULES.dev };

export default function UsersPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ ...EMPTY, modules: [...DEFAULT_MODULES.dev] }); setOpen(true); };
  const openEdit = (u) => { setEditing(u); setForm({ email: u.email, name: u.name, role: u.role, password: "", modules: u.modules || DEFAULT_MODULES[u.role] || [] }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("El nombre es obligatorio"); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/users/${editing.id}`, { name: form.name, email: form.email, role: form.role, password: form.password || undefined, modules: form.modules });
        toast.success("Usuario actualizado");
      } else {
        await api.post("/users", { email: form.email, name: form.name, role: form.role, password: form.password, modules: form.modules });
        toast.success("Usuario creado");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const setRole = (role) => setForm((f) => ({ ...f, role, modules: DEFAULT_MODULES[role] || f.modules }));
  const toggleModule = (m) => setForm((f) => ({ ...f, modules: f.modules.includes(m) ? f.modules.filter((x) => x !== m) : [...f.modules, m] }));

  const confirmDelete = async () => {
    try {
      await api.delete(`/users/${toDelete.id}`);
      toast.success("Usuario eliminado");
      setToDelete(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div>
      <div className="bg-white border-b border-border px-8 py-6 flex items-center justify-between">
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400">Administración</p>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight mt-0.5 flex items-center gap-2"><Users className="h-7 w-7" />Usuarios</h1>
        </div>
        <Button data-testid="new-user-btn" onClick={openNew} className="rounded-sm bg-slate-900 hover:bg-slate-800">
          <Plus className="h-4 w-4 mr-1" /> Nuevo usuario
        </Button>
      </div>

      <div className="p-8">
        <div className="border border-border bg-white overflow-x-auto" data-testid="users-table">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead className="text-[11px] font-bold uppercase tracking-wide text-slate-600">Usuario</TableHead>
                <TableHead className="text-[11px] font-bold uppercase tracking-wide text-slate-600">Correo</TableHead>
                <TableHead className="text-[11px] font-bold uppercase tracking-wide text-slate-600">Rol</TableHead>
                <TableHead className="w-20"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id} data-testid={`user-row-${u.email}`} className="hover:bg-slate-50 transition-colors duration-200">
                  <TableCell>
                    <div className="flex items-center gap-2.5"><Avatar user={u} size={30} /><span className="font-medium">{u.name}</span></div>
                  </TableCell>
                  <TableCell className="font-mono text-xs text-slate-500">{u.email}</TableCell>
                  <TableCell>
                    <span className={`inline-flex items-center rounded-sm px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide ${ROLE_BADGE[u.role]}`}>
                      {ROLE_LABELS[u.role]}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-slate-900" data-testid={`edit-user-${u.email}`} onClick={() => openEdit(u)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      {u.id !== me.id && (
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-red-600" data-testid={`delete-user-${u.email}`} onClick={() => setToDelete(u)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-md" data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-extrabold tracking-tight">{editing ? "Editar usuario" : "Nuevo usuario"}</DialogTitle>
            <DialogDescription className="text-slate-500 text-sm">Define el acceso y el rol del miembro del equipo.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Nombre</Label>
              <Input data-testid="user-name-input" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="mt-1.5 rounded-sm" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Correo</Label>
              <Input data-testid="user-email-input" type="email" value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className="mt-1.5 rounded-sm" placeholder="usuario@empresa.com" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Rol</Label>
              <Select value={form.role} onValueChange={setRole}>
                <SelectTrigger data-testid="user-role-select" className="mt-1.5 rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(ROLE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Módulos con acceso</Label>
              <div className="mt-2 grid grid-cols-2 gap-1.5" data-testid="user-modules">
                {MODULE_ORDER.map((m) => (
                  <div key={m} role="button" data-testid={`module-${m}`} onClick={() => toggleModule(m)}
                    className={`flex items-center gap-2 text-sm py-1.5 px-2 rounded-sm cursor-pointer border transition-colors duration-150 ${form.modules.includes(m) ? "bg-slate-100 border-slate-300" : "border-slate-200 hover:bg-slate-50"}`}>
                    <Checkbox checked={form.modules.includes(m)} className="pointer-events-none" tabIndex={-1} />
                    {MODULES[m]}
                  </div>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{editing ? "Nueva contraseña (opcional)" : "Contraseña"}</Label>
              <Input data-testid="user-password-input" type="password" value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} className="mt-1.5 rounded-sm" placeholder="Mínimo 6 caracteres" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button data-testid="user-save-btn" className="rounded-sm bg-slate-900 hover:bg-slate-800" onClick={save} disabled={saving}>{saving ? "Guardando..." : "Guardar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar usuario?</AlertDialogTitle>
            <AlertDialogDescription>Se eliminará <strong>{toDelete?.name}</strong> y se desasignará de sus desarrollos y tareas.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-user" onClick={confirmDelete} className="rounded-sm bg-red-600 hover:bg-red-700">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
